import type { CppInvoiceTotals, CppInvoiceTransaction } from '~/services/LuxLoyaltyService.types';

/** A ledger row stamped with the partner it belongs to. The endpoint itself does not repeat the name. */
export interface PartnerTransactionRow extends CppInvoiceTransaction {
  partnerName: string;
  /** That partner's billing currency. Null when the partner has no rate card. */
  currency: string | null;
}

/** One partner's walked pages, ready to merge with the others. */
export interface PartnerTransactionGroup {
  partnerName: string;
  currency: string | null;
  transactions: Array<CppInvoiceTransaction>;
}

/**
 * Combines each partner's ledger into one date-ordered list.
 *
 * The transactions endpoint is scoped to a single partner, so an all-partners view is assembled
 * here. Order matches the single-partner grid: Sydney day, then the row's timestamp.
 */
export function mergePartnerTransactions(groups: Array<PartnerTransactionGroup>): Array<PartnerTransactionRow> {
  return groups
    .flatMap((group) =>
      group.transactions.map((transaction) => ({
        ...transaction,
        partnerName: group.partnerName,
        currency: group.currency,
      })),
    )
    .sort(
      (a, b) =>
        a.date.localeCompare(b.date) ||
        a.createdAt.localeCompare(b.createdAt) ||
        a.transactionId.localeCompare(b.transactionId),
    );
}

/** Empty totals, the identity for {@link sumInvoiceTotals}. */
function emptyTotals(): CppInvoiceTotals {
  return {
    creditPoints: 0,
    debitPoints: 0,
    refundPoints: 0,
    netPoints: 0,
    creditBonusPoints: 0,
    debitBonusPoints: 0,
    refundBonusPoints: 0,
    netBonusPoints: 0,
    statusCredits: 0,
    tierUpgradeCount: 0,
    amountCents: 0,
    unpricedNetPoints: 0,
    unpricedNetBonusPoints: 0,
    excludedRefundCount: 0,
    pendingTransactionCount: 0,
  };
}

/**
 * Adds whole-period totals across partners.
 *
 * Each partner's totals already cover that partner's full period, so this is a sum of those
 * figures rather than a re-price of the merged rows.
 */
export function sumInvoiceTotals(totals: Array<CppInvoiceTotals>): CppInvoiceTotals {
  return totals.reduce(
    (sum, next) => ({
      creditPoints: sum.creditPoints + next.creditPoints,
      debitPoints: sum.debitPoints + next.debitPoints,
      refundPoints: sum.refundPoints + next.refundPoints,
      netPoints: sum.netPoints + next.netPoints,
      creditBonusPoints: sum.creditBonusPoints + next.creditBonusPoints,
      debitBonusPoints: sum.debitBonusPoints + next.debitBonusPoints,
      refundBonusPoints: sum.refundBonusPoints + next.refundBonusPoints,
      netBonusPoints: sum.netBonusPoints + next.netBonusPoints,
      statusCredits: sum.statusCredits + next.statusCredits,
      tierUpgradeCount: sum.tierUpgradeCount + next.tierUpgradeCount,
      amountCents: sum.amountCents + next.amountCents,
      unpricedNetPoints: sum.unpricedNetPoints + next.unpricedNetPoints,
      unpricedNetBonusPoints: sum.unpricedNetBonusPoints + next.unpricedNetBonusPoints,
      excludedRefundCount: sum.excludedRefundCount + next.excludedRefundCount,
      pendingTransactionCount: sum.pendingTransactionCount + next.pendingTransactionCount,
    }),
    emptyTotals(),
  );
}

/**
 * The currency shared by every partner, or mixed when they are not the same.
 *
 * A missing currency (a partner with no rate card) does not count as a second currency.
 */
export function resolveSharedCurrency(currencies: Array<string | null>): { currency: string | null; mixed: boolean } {
  const present = [...new Set(currencies.map((currency) => currency?.trim()).filter((currency) => Boolean(currency)))];

  if (present.length > 1) {
    return { currency: null, mixed: true };
  }

  return { currency: present[0] ?? null, mixed: false };
}
